import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Clock, CheckCircle, XCircle, Warning, BellRinging } from '@phosphor-icons/react';

/**
 * Magic-link landing page for waitlist offers.
 * URL: /waitlist/confirm?token=<token>
 *
 * Flow:
 *  - Fetch GET /api/waitlist/confirm/<token> (no auth) → render details
 *  - If user is not logged-in or logged-in as a different account → ask to log in
 *  - If correct user → "Bestätigen & bezahlen" → POST confirm → /payments/checkout → Datatrans
 */
const WaitlistConfirm = () => {
  const { user, API, language, t } = useAuth();
  const dateLocale = language === 'de' ? de : enUS;
  const location = useLocation();
  const navigate = useNavigate();

  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!token) {
      setError(language === 'de' ? 'Ungültiger Link.' : 'Invalid link.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await API.get(`/waitlist/confirm/${token}`);
        setDetails(data);
      } catch (e) {
        setError(e.response?.data?.detail || (language === 'de' ? 'Link nicht gefunden.' : 'Link not found.'));
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const minutesLeft = useMemo(() => {
    if (!details?.confirmation_expires_at) return null;
    const exp = new Date(details.confirmation_expires_at).getTime();
    const diff = Math.max(0, Math.floor((exp - now) / 1000));
    const mm = Math.floor(diff / 60).toString().padStart(2, '0');
    const ss = (diff % 60).toString().padStart(2, '0');
    return { diff, label: `${mm}:${ss}` };
  }, [details, now]);

  const handleConfirm = async () => {
    if (!user) {
      // Send to login with redirect param back to this page
      const next = encodeURIComponent(`/waitlist/confirm?token=${token}`);
      navigate(`/login?next=${next}`);
      return;
    }
    setConfirming(true);
    try {
      const { data } = await API.post(`/waitlist/confirm/${token}`);
      const bookingId = data.booking_id;
      const co = await API.post('/payments/checkout', {
        booking_id: bookingId,
        origin_url: window.location.origin,
      });
      window.location.href = co.data.url;
    } catch (e) {
      const msg = e.response?.data?.detail || (language === 'de' ? 'Bestätigung fehlgeschlagen.' : 'Confirmation failed.');
      toast.error(msg);
      setError(msg);
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-xl mx-auto p-8 text-center text-slate-500" data-testid="waitlist-confirm-loading">
        {language === 'de' ? 'Lade Angebot…' : 'Loading offer…'}
      </div>
    );
  }

  if (error && !details) {
    return (
      <div className="max-w-xl mx-auto p-8" data-testid="waitlist-confirm-error">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex gap-4 items-start">
          <XCircle size={28} className="text-red-600 shrink-0" />
          <div>
            <h2 className="text-lg font-bold text-red-700 mb-1">
              {language === 'de' ? 'Link ungültig oder abgelaufen' : 'Link invalid or expired'}
            </h2>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!details) return null;

  const dateLabel = details.date ? format(new Date(`${details.date}T00:00:00`), 'PPPP', { locale: dateLocale }) : '';
  const expiredFlag = details.expired || (minutesLeft && minutesLeft.diff === 0);
  const wrongUser = user && details.user_id && user.id !== details.user_id;

  return (
    <div className="max-w-xl mx-auto p-4 md:p-8 space-y-6 animate-fade-in" data-testid="waitlist-confirm">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 flex items-center justify-center">
          <BellRinging size={26} weight="duotone" className="text-emerald-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {language === 'de' ? 'Platz frei geworden!' : 'A court is free!'}
          </h1>
          <p className="text-sm text-slate-500">
            {language === 'de' ? 'Du wurdest von der Warteliste eingeladen.' : "You're up next on the waitlist."}
          </p>
        </div>
      </div>

      {/* Slot details */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 space-y-2">
        <div className="flex justify-between"><span className="text-slate-500">{language === 'de' ? 'Platz' : 'Court'}</span><span className="font-bold text-slate-900">{details.offer_court_name || '—'}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">{language === 'de' ? 'Datum' : 'Date'}</span><span className="text-slate-900">{dateLabel}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">{language === 'de' ? 'Zeit' : 'Time'}</span><span className="text-slate-900">{details.start_time} ({details.offer_duration_minutes} {language === 'de' ? 'Min' : 'min'})</span></div>
        <div className="flex justify-between pt-2 border-t border-emerald-200/70"><span className="text-slate-600 font-semibold">{language === 'de' ? 'Preis' : 'Price'}</span><span className="font-black text-emerald-700 text-xl">{Number(details.offer_amount || 0).toFixed(2)} CHF</span></div>
      </div>

      {/* Countdown */}
      {!expiredFlag && minutesLeft && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-center gap-3" data-testid="waitlist-countdown">
          <Clock size={22} className="text-amber-600 shrink-0" />
          <div className="flex-1">
            <div className="text-xs font-bold uppercase text-amber-700 tracking-wide">{language === 'de' ? 'Verbleibende Zeit' : 'Time left'}</div>
            <div className="text-2xl font-black text-amber-700 tabular-nums">{minutesLeft.label}</div>
          </div>
          <span className="text-xs text-amber-700 max-w-[10rem] text-right">
            {language === 'de' ? 'Bestätige jetzt, sonst geht das Angebot weiter.' : 'Confirm now or the offer rolls to the next person.'}
          </span>
        </div>
      )}

      {/* Status banners */}
      {details.status === 'confirmed' && (
        <div className="bg-emerald-100 border border-emerald-300 rounded-xl p-4 flex gap-3 items-start">
          <CheckCircle size={22} className="text-emerald-700 shrink-0" />
          <div className="text-sm text-emerald-800">
            {language === 'de' ? 'Du hast dieses Angebot bereits bestätigt. Schau in deinen Buchungen nach.' : 'You already confirmed this offer — check your bookings.'}
          </div>
        </div>
      )}

      {expiredFlag && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3 items-start" data-testid="waitlist-expired">
          <Warning size={22} className="text-red-600 shrink-0" />
          <div className="text-sm text-red-700">
            {language === 'de' ? 'Dieses Angebot ist abgelaufen. Der/die nächste Wartende wurde informiert.' : 'This offer has expired. The next person in line has been notified.'}
          </div>
        </div>
      )}

      {wrongUser && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800" data-testid="waitlist-wrong-user">
          {language === 'de'
            ? `Dieses Angebot gehört zu ${details.user_name || 'einer anderen Person'}. Bitte melde dich mit dem richtigen Konto an.`
            : `This offer belongs to ${details.user_name || 'another account'}. Please log in with the correct account.`}
        </div>
      )}

      {/* CTA */}
      {!expiredFlag && details.status === 'notified' && !wrongUser && (
        <Button
          onClick={handleConfirm}
          disabled={confirming}
          className="w-full btn-primary h-14 text-base"
          data-testid="waitlist-confirm-btn"
        >
          {confirming
            ? (language === 'de' ? 'Verarbeite…' : 'Processing…')
            : user
              ? (language === 'de' ? 'Bestätigen & bezahlen' : 'Confirm & pay')
              : (language === 'de' ? 'Einloggen, um zu bestätigen' : 'Log in to confirm')}
        </Button>
      )}

      <p className="text-xs text-slate-400 text-center">
        {language === 'de'
          ? 'Padelarena Wädenswil · Warteliste'
          : 'Padelarena Wädenswil · Waitlist'}
      </p>
    </div>
  );
};

export default WaitlistConfirm;
